// frontend/src/pages/ContentBlockRenderer.js — FULL REPLACEMENT (Phase C)
// Backward compatible: same exports (resolveImageUrl, stripHtml,
// resolveYoutubeEmbed, MermaidRenderer, default renderer) so
// AdminContentEditor and existing pages keep working unchanged.
//
// New in this version:
//   1. API_BASE now comes from services/api.js (no http:// IP fallback →
//      fixes mixed-content risk inside the HTTPS Android WebView)
//   2. flowchart: if content_data.svg_url exists (Phase D server-side render)
//      it displays the SVG image; otherwise falls back to the mermaid iframe.
//      → Phase D will NOT need to touch this file again.
//   3. table: on mobile, tables with more than 3 columns reflow into stacked
//      "header: value" cards — no horizontal scrolling inside vertical scroll.
//   4. Optional onAskAi(block) prop: when provided, a small "Ask AI" chip is
//      rendered under supported blocks (wired in Phase E; harmless until then).

import React, { useState, useEffect, useRef } from 'react';
import { API_BASE } from '../services/api.js';
import { buildWorksheetDoc } from '../utils/excelWorksheet';
import VideoPlayerFullscreen from '../components/VideoPlayerFullscreen';
import { useLanguage } from '../contexts/languagecontext';

export const resolveImageUrl = (path) => {
    if (!path) return '';
    return path.startsWith('http') ? path : `${API_BASE}/${path}`;
};

export const stripHtml = (html) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
};

export const resolveYoutubeEmbed = (url) => {
    if (!url) return '';
    const match = url.match(/(?:youtu\.be\/|watch\?v=|shorts\/|embed\/)([a-zA-Z0-9_-]+)/);
    const id = match ? match[1] : '';
    return `https://www.youtube.com/embed/${id}`;
};


// Inlines a stored flowchart SVG (via fetch) so <foreignObject> labels render
// in the Android WebView, which does NOT render them through <img>.
export const InlineSvg = ({ url, caption, onMediaClick }) => {
    const [markup, setMarkup] = useState(null);
    const src = resolveImageUrl(url);

    useEffect(() => {
        let alive = true;
        fetch(src)
            .then((r) => r.text())
            .then((txt) => {
                if (!alive) return;
                // Strip fixed width/height so the SVG scales to its container
                // (viewBox is kept, so aspect ratio is preserved).
                let cleaned = txt
                    .replace(/(<svg[^>]*?)\swidth="[^"]*"/i, '$1')
                    .replace(/(<svg[^>]*?)\sheight="[^"]*"/i, '$1')
                    .replace(/(<svg[^>]*?)\sstyle="[^"]*"/i, '$1');
                cleaned = cleaned.replace(
                    /<svg\b/i,
                    '<svg style="width:100%;height:auto;display:block"'
                );
                setMarkup(cleaned);
            })
            .catch(() => { if (alive) setMarkup(null); });
        return () => { alive = false; };
    }, [src]);

    if (markup === null) return null;

    return (
        <div className="stc-flowchart-svg">
            <div
                className="stc-flowchart-inline"
                style={{ width: '100%', maxWidth: '640px', margin: '0 auto' }}
                onClick={() => onMediaClick && onMediaClick({ type: 'image', url: src, caption })}
                dangerouslySetInnerHTML={{ __html: markup }}
            />
            {caption && <p className="stc-caption">{caption}</p>}
        </div>
    );
};




// ─────────────────────────────────────────────────────────────────────────────
// Mermaid iframe fallback (kept for charts not yet rendered to SVG)
// ─────────────────────────────────────────────────────────────────────────────
export const MermaidRenderer = ({ code, caption }) => {
    const [height, setHeight] = useState(300);

    useEffect(() => {
        const handler = (e) => {
            if (e.data?.type === 'mermaid-height') setHeight(e.data.height + 20);
        };
        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []);

    const iframeDoc = `<!DOCTYPE html>
<html><head><meta name="viewport" content="width=device-width, initial-scale=1.0">
<style>body{margin:8px;padding:0;overflow-x:auto;}.mermaid svg{max-width:none;}</style></head>
<body><div class="mermaid">${code}</div>
<script src="https://cdn.jsdelivr.net/npm/mermaid@10/dist/mermaid.min.js"><\/script>
<script>
mermaid.initialize({ startOnLoad: true, theme: 'default', flowchart: { useMaxWidth: true } });
window.addEventListener('load', function() {
  setTimeout(function() {
    parent.postMessage({ type: 'mermaid-height', height: document.body.scrollHeight }, '*');
  }, 1000);
});
<\/script></body></html>`;

    return (
        <div>
            <iframe srcDoc={iframeDoc} style={{ width: '100%', border: 'none', height: `${height}px` }} title="Flowchart" />
            {caption && <p className="stc-caption">{caption}</p>}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Carousels
// ─────────────────────────────────────────────────────────────────────────────
const ImageCarousel = ({ images, onMediaClick }) => {
    const [current, setCurrent] = useState(0);
    if (!images || images.length === 0) return null;
    const img = images[current];
    const caption = img.en?.caption || img.caption || '';
    const imgUrl = img.url || img.image_url || img.file_path || img.path;

    return (
        <div className="stc-carousel-single">
            <div className="stc-carousel-frame">
                <img
                    src={resolveImageUrl(imgUrl)}
                    alt=""
                    className="stf-content-img"
                    style={{ width: '100%', height: 'auto', maxHeight: 'none', minHeight: '20px', display: 'block' }}
                    onClick={() => onMediaClick && onMediaClick({ type: 'image', url: resolveImageUrl(imgUrl), caption })}
                />
                {images.length > 1 && (
                    <>
                        <button className="stc-carousel-arrow stc-carousel-arrow-left"
                            onClick={() => setCurrent((c) => (c === 0 ? images.length - 1 : c - 1))}>&#8249;</button>
                        <button className="stc-carousel-arrow stc-carousel-arrow-right"
                            onClick={() => setCurrent((c) => (c === images.length - 1 ? 0 : c + 1))}>&#8250;</button>
                    </>
                )}
            </div>
            {caption && <p className="stc-caption">{caption}</p>}
            {images.length > 1 && (
                <div className="stc-carousel-dots">
                    {images.map((_, i) => (
                        <span key={i}
                            className={`stc-carousel-dot ${i === current ? 'stc-carousel-dot-active' : ''}`}
                            onClick={() => setCurrent(i)} />
                    ))}
                </div>
            )}
        </div>
    );
};

const VideoCarousel = ({ videos, orientation = 'horizontal' }) => {
    const [current, setCurrent] = useState(0);
    const [open, setOpen] = useState(false);
    if (!videos || videos.length === 0) return null;
    const vid = videos[current];
    const caption = vid.en?.caption || vid.caption || '';
    const embedUrl = vid.youtube_id
        ? `https://www.youtube.com/embed/${vid.youtube_id}${vid.start_seconds ? `?start=${vid.start_seconds}` : ''}`
        : resolveYoutubeEmbed(vid.video_url || vid.url);
    const idMatch = (embedUrl || '').match(/embed\/([a-zA-Z0-9_-]+)/);
    const thumb = idMatch ? `https://img.youtube.com/vi/${idMatch[1]}/hqdefault.jpg` : null;

    return (
        <div className="stc-carousel-single">
            <div className="stc-carousel-frame">
                <div
                    className={`stc-video-thumb ${orientation === 'vertical' ? 'stc-video-thumb-vertical' : ''}`}
                    onClick={() => embedUrl && setOpen(true)}
                    role="button"
                >
                    {thumb && <img src={thumb} alt={caption || `video-${current}`} />}
                    <span className="stc-video-play">▶</span>
                </div>
                {videos.length > 1 && (
                    <>
                        <button className="stc-carousel-arrow stc-carousel-arrow-left"
                            onClick={() => setCurrent((c) => (c === 0 ? videos.length - 1 : c - 1))}>&#8249;</button>
                        <button className="stc-carousel-arrow stc-carousel-arrow-right"
                            onClick={() => setCurrent((c) => (c === videos.length - 1 ? 0 : c + 1))}>&#8250;</button>
                    </>
                )}
            </div>
            {caption && <p className="stc-caption">{caption}</p>}
            {videos.length > 1 && (
                <div className="stc-carousel-dots">
                    {videos.map((_, i) => (
                        <span key={i}
                            className={`stc-carousel-dot ${i === current ? 'stc-carousel-dot-active' : ''}`}
                            onClick={() => setCurrent(i)} />
                    ))}
                </div>
            )}
            {open && embedUrl && (
                <VideoPlayerFullscreen
                    embedUrl={embedUrl}
                    orientation={orientation}
                    title={caption}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
};

const VideoBlock = ({ embedUrl, orientation, caption }) => {
    const [open, setOpen] = useState(false);
    const idMatch = embedUrl.match(/embed\/([a-zA-Z0-9_-]+)/);
    const thumb = idMatch ? `https://img.youtube.com/vi/${idMatch[1]}/hqdefault.jpg` : null;
    return (
        <div className="stc-video-block">
            <div
                className={`stc-video-thumb ${orientation === 'vertical' ? 'stc-video-thumb-vertical' : ''}`}
                onClick={() => setOpen(true)}
                role="button"
            >
                {thumb && <img src={thumb} alt={caption || 'video'} />}
                <span className="stc-video-play">▶</span>
            </div>
            {caption && <p className="stc-caption">{caption}</p>}
            {open && (
                <VideoPlayerFullscreen
                    embedUrl={embedUrl}
                    orientation={orientation}
                    title={caption}
                    onClose={() => setOpen(false)}
                />
            )}
        </div>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Table: always the normal grid table, with horizontal scroll on overflow
// ─────────────────────────────────────────────────────────────────────────────
const TableBlock = ({ data }) => {
    const columns = data.columns || [];
    const rows = data.rows || [];
    const headers = columns.map((c) => (typeof c === 'object' ? c.header : c));

    return (
        <div className={`stc-table stc-table-${data.table_style || 'default'}`}>
            {data.title && <div className="stc-table-title">{data.title}</div>}
            <div className="stc-table-scroll">
                <table>
                    {data.has_header !== false && headers.length > 0 && (
                        <thead><tr>{headers.map((h, i) => <th key={i}>{h}</th>)}</tr></thead>
                    )}
                    <tbody>
                        {rows.map((row, ri) => (
                            <tr key={ri}>{row.map((cell, ci) => <td key={ci}>{cell}</td>)}</tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};


// ─────────────────────────────────────────────────────────────────────────────
// Worksheet: Excel-pasted HTML in an isolated, auto-height iframe.
// Real borders/fills preserved; no gridlines; wide tables scroll horizontally.
// ─────────────────────────────────────────────────────────────────────────────
export const WorksheetBlock = ({ data }) => {
    const frameRef = useRef(null);
    const html  = data?.content || data?.html || '';
    const title = data?.title;

    const fit = () => {
        const f = frameRef.current;
        if (f && f.contentDocument && f.contentDocument.body) {
            f.style.height = (f.contentDocument.body.scrollHeight + 24) + 'px';
        }
    };

    useEffect(() => {
        fit();
        window.addEventListener('resize', fit);
        return () => window.removeEventListener('resize', fit);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [html]);

    if (!html) return null;

    return (
        <div className="stc-worksheet">
            {title && <div className="stc-table-title">{title}</div>}
            <iframe
                ref={frameRef}
                srcDoc={buildWorksheetDoc(html)}
                className="stc-worksheet-frame"
                title={title || 'Worksheet'}
                onLoad={fit}
                sandbox="allow-same-origin"
                style={{ width: '100%', border: 'none', display: 'block', background: 'transparent' }}
            />
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Main renderer
// ─────────────────────────────────────────────────────────────────────────────
const ContentBlockRenderer = ({ block, onMediaClick, onAskAi, askAiTypes }) => {
    const data = block.content_data || {};
    const { hideImages } = useLanguage();

    const askAiChip = onAskAi && (askAiTypes && askAiTypes.length ? askAiTypes : ['text', 'article', 'table', 'flowchart', 'steps', 'html', 'worksheet']).includes(block.block_type) ? (
        <button className="stc-askai-chip" onClick={() => onAskAi(block)}>✦ Ask AI</button>
    ) : null;

    let body = null;

    switch (block.block_type) {
        case 'article':
            body = (
                <div>
                    {data.heading && (
                        <div className="stc-article-heading" dangerouslySetInnerHTML={{ __html: data.heading }} />
                    )}
                    {!hideImages && data.feature_image && (
                        <img
                            src={resolveImageUrl(data.feature_image)}
                            alt=""
                            className="stf-content-img"
                            style={{ width: '100%', height: 'auto', maxHeight: 'none', minHeight: '20px', display: 'block' }}
                            style={{ maxWidth: '100%', height: 'auto', display: 'block' }}
                            onClick={() => onMediaClick && onMediaClick({ type: 'image', url: resolveImageUrl(data.feature_image), caption: stripHtml(data.heading) })}
                        />
                    )}
                    {data.short_summary && <p className="stc-article-summary">{data.short_summary}</p>}
                    {(data.content || data.full_text) && (
                        <div className="stc-article-body" dangerouslySetInnerHTML={{ __html: data.content || data.full_text }} />
                    )}
                </div>
            );
            break;

        case 'text':
            body = <div className="stc-text" dangerouslySetInnerHTML={{ __html: data.content }} />;
            break;

        case 'image': {
            if (hideImages) return null;
            const url = data.url || data.image_url || data.file_path || data.path;
            if (!url) return null;
            body = (
                <div className="stc-image-block">
                    <img
                        src={resolveImageUrl(url)}
                        alt={data.caption || ''}
                        className="stf-content-img"
                        style={{ width: '100%', height: 'auto', maxHeight: 'none', minHeight: '20px', display: 'block' }}
                        onClick={() => onMediaClick && onMediaClick({ type: 'image', url: resolveImageUrl(url), caption: data.caption })}
                    />
                    {data.caption && <p className="stc-caption">{data.caption}</p>}
                </div>
            );
            break;
        }

        case 'video': {
            if (hideImages) return null;
            const embedSrc = data.youtube_id
                ? `https://www.youtube.com/embed/${data.youtube_id}${data.start_seconds ? `?start=${data.start_seconds}` : ''}`
                : resolveYoutubeEmbed(data.video_url || data.url);
            body = embedSrc ? (
                <VideoBlock
                    embedUrl={embedSrc}
                    orientation={data.orientation || 'horizontal'}
                    caption={data.caption}
                />
            ) : null;
            break;
        }

        case 'table':
            body = <TableBlock data={data} />;
            break;

        case 'flowchart':
            if (data.svg_url) {
                body = <InlineSvg url={data.svg_url} caption={data.caption} onMediaClick={onMediaClick} />;
            } else {
                body = <MermaidRenderer code={data.mermaid_code} caption={data.caption} />;
            }
            break;

        case 'steps':
            body = (
                <div className="stc-steps">
                    {data.heading && <p className="stc-steps-heading">{data.heading}</p>}
                    {data.steps?.map((step, si) => (
                        <div key={si} className="stc-step">
                            <div className="stc-step-main">
                                <span className="stc-step-num">Step {si + 1}</span>
                                <span className="stc-step-content">{step.content}</span>
                            </div>
                            {step.explanation && (
                                <div className="stc-step-explanation">{step.explanation}</div>
                            )}
                        </div>
                    ))}
                </div>
            );
            break;

        case 'html': {
            const htmlContent = data.content || data.html || data.html_code || '';
            const iframeDoc = htmlContent.trim().toLowerCase().startsWith('<!doctype') || htmlContent.trim().toLowerCase().startsWith('<html')
                ? htmlContent
                : `<!DOCTYPE html><html><head><meta name="viewport" content="width=device-width, initial-scale=1.0"><style>* { box-sizing: border-box; } body { margin: 8px; font-family: Arial, sans-serif; font-size: 14px; } img { max-width: 100%; height: auto; } * { max-width: 100%; }</style></head><body>${htmlContent}</body></html>`;
            body = (
                <iframe
                    srcDoc={iframeDoc}
                    className="stc-html-block"
                    style={{ width: '100%', border: 'none', minHeight: '200px' }}
                    onLoad={(e) => {
                        const iframe = e.target;
                        const bodyEl = iframe.contentDocument?.body;
                        if (bodyEl) {
                            iframe.style.height = bodyEl.scrollHeight + 20 + 'px';
                        }
                    }}
                    title="HTML Content"
                    sandbox="allow-scripts allow-same-origin"
                />
            );
            break;
        }

        case 'worksheet':
            body = <WorksheetBlock data={data} />;
            break;
        
        case 'image_carousel':
            body = hideImages ? null : <ImageCarousel images={data.images} onMediaClick={onMediaClick} />;
            break;

        case 'video_carousel':
            body = hideImages ? null : <VideoCarousel videos={data.videos} orientation={data.orientation || 'horizontal'} />;
            break;

        default:
            return null;
    }

    if (body === null) return null;
    
    return (
        <div className="stc-block-wrap">
            {body}
            {askAiChip}
        </div>
    );
};

export default ContentBlockRenderer;